import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { getCurrentClinicId } from "@/lib/utils/clinic-context";
import { normalizeRelationshipPair, type PetRelationshipKind } from "./petProfileModel";
import { petReservationSummaryKey } from "./usePetReservationSummary";

export type PetAlertInput = {
  petId: string;
  customerId: string;
  label: string;
  value: string;
  icon: string | null;
  color: string | null;
};

export type PetConditionsInput = {
  petId: string;
  allergyIds: string[];
  temperamentIds: string[];
};

export type RelationshipInput = {
  customerId: string;
  petId: string;
  relatedPetId: string;
  relationship: PetRelationshipKind;
  description?: string | null;
};

type PetRelationshipInsertRow = Database["public"]["Tables"]["pet_relationships"]["Insert"] & {
  description: string | null;
};

type PetRelationshipsWriteTable = {
  upsert: (
    row: PetRelationshipInsertRow,
    options: { onConflict: string },
  ) => Promise<{ error: Error | null }>;
};

const petRelationshipsWriteTable = () =>
  (supabase as unknown as { from: (table: "pet_relationships") => PetRelationshipsWriteTable }).from("pet_relationships");

export function useRelationshipPetOptions(customerId: string | null, petId: string) {
  return useQuery({
    queryKey: ["relationship-pet-options", customerId, petId],
    enabled: !!customerId && !!petId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pets")
        .select("id, name")
        .neq("id", petId)
        .is("deleted_at", null)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function createRelationshipRow({ customerId, petId, relatedPetId, relationship, description }: RelationshipInput): PetRelationshipInsertRow {
  if (petId === relatedPetId) {
    throw new Error("A pet cannot have a relationship with itself");
  }
  const pair = normalizeRelationshipPair(petId, relatedPetId);
  return {
    customer_id: customerId,
    pet_id: pair.petId,
    related_pet_id: pair.relatedPetId,
    relationship,
    description: description ?? null,
  };
}

function useInvalidatePetSummary() {
  const queryClient = useQueryClient();
  return (petId: string) => {
    queryClient.invalidateQueries({ queryKey: petReservationSummaryKey(petId) });
    // O painel do dia lê pet_custom_alerts/pet_conditions/pet_relationships
    // direto (usePanelDay.ts), fora do resumo do pet — sem isso, alertas e
    // afins só apareciam atualizados depois de um reload manual.
    queryClient.invalidateQueries({ queryKey: ["panel-day"] });
  };
}

export function useCreatePetAlert() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async (input: PetAlertInput) => {
      const { error } = await supabase.from("pet_custom_alerts").insert({
        pet_id: input.petId,
        customer_id: input.customerId,
        label: input.label,
        value: input.value,
        icon: input.icon,
        color: input.color,
      });
      if (error) throw error;
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}

export type PetAlertUpdate = { id: string; petId: string; label: string; value: string; color: string | null };

export function useUpdatePetAlert() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async (input: PetAlertUpdate) => {
      const { error } = await supabase
        .from("pet_custom_alerts")
        .update({ label: input.label, value: input.value, color: input.color })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}

export function useDeactivatePetAlert() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async ({ id }: { id: string; petId: string }) => {
      const { error } = await supabase.from("pet_custom_alerts").update({ is_active: false }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}

export function useUpdatePetConditions() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async (input: PetConditionsInput) => {
      const { data: existing, error: existingError } = await supabase
        .from("pet_conditions")
        .select("condition_id, condition:conditions(type)")
        .eq("pet_id", input.petId);
      if (existingError) throw existingError;

      const staleConditionIds = (existing ?? [])
        .filter((row) => {
          const type = (row.condition as { type: string } | null)?.type;
          return type === "allergy" || type === "temperament";
        })
        .map((row) => row.condition_id);

      if (staleConditionIds.length > 0) {
        const { error: deleteError } = await supabase
          .from("pet_conditions")
          .delete()
          .eq("pet_id", input.petId)
          .in("condition_id", staleConditionIds);
        if (deleteError) throw deleteError;
      }

      const newConditionIds = [...input.allergyIds, ...input.temperamentIds];
      if (newConditionIds.length > 0) {
        const { error: insertError } = await supabase
          .from("pet_conditions")
          .insert(newConditionIds.map((conditionId) => ({ pet_id: input.petId, condition_id: conditionId })));
        if (insertError) throw insertError;
      }
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}

export function useUpsertPetRelationship() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async (input: RelationshipInput) => {
      const { error } = await petRelationshipsWriteTable()
        .upsert(createRelationshipRow(input), { onConflict: "pet_id,related_pet_id" });
      if (error) throw error;
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}

export function useDeletePetRelationship() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async ({ id }: { id: string; petId: string }) => {
      const { error } = await supabase.from("pet_relationships").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}

export type StoredItemInput = { petId: string; itemName: string; storageLocation: string | null };

export function createStoredItemRow({
  petId,
  clinicId,
  itemName,
  storageLocation,
}: {
  petId: string;
  clinicId: string;
  itemName: string;
  storageLocation: string | null;
}) {
  return {
    pet_id: petId,
    clinic_id: clinicId,
    item_name: itemName,
    storage_location: storageLocation,
    is_kept: true,
  } satisfies Database["public"]["Tables"]["pet_stored_items"]["Insert"];
}

export function useCreatePetStoredItem() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async (input: StoredItemInput) => {
      const clinicId = await getCurrentClinicId();
      const { error } = await supabase
        .from("pet_stored_items")
        .insert(createStoredItemRow({ petId: input.petId, clinicId, itemName: input.itemName, storageLocation: input.storageLocation }));
      if (error) throw error;
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}

export function useUpdatePetStoredItem() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async (input: { id: string; petId: string; itemName: string; storageLocation: string | null }) => {
      const { error } = await supabase
        .from("pet_stored_items")
        .update({ item_name: input.itemName, storage_location: input.storageLocation })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}

export function useUnkeepPetStoredItem() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async (input: { id: string; petId: string }) => {
      const { error } = await supabase.from("pet_stored_items").update({ is_kept: false }).eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}

export function useReactivatePetAlert() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async ({ id }: { id: string; petId: string }) => {
      const { error } = await supabase.from("pet_custom_alerts").update({ is_active: true }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}

export function useKeepPetStoredItem() {
  const invalidate = useInvalidatePetSummary();
  return useMutation({
    mutationFn: async ({ id }: { id: string; petId: string }) => {
      const { error } = await supabase.from("pet_stored_items").update({ is_kept: true }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, input) => invalidate(input.petId),
  });
}
